import { useRef, useCallback } from "react";

/** Status upgrade priority — higher = better, never downgrade */
const STATUS_PRIORITY = { pending: 0, failed: 0, delivered: 1, seen: 2 };

/**
 * Union-merge two readBy arrays.
 * Always keeps more entries; never reduces. Returns the existing array
 * reference unchanged when nothing new is added (avoids spurious re-renders).
 */
const mergeReadByArrays = (existing = [], incoming = []) => {
  if (!incoming?.length) return existing || [];
  const existingIds = new Set(
    (existing || []).map((r) => String(r.userId?._id || r.userId)),
  );
  const toAdd = incoming.filter(
    (r) => !existingIds.has(String(r.userId?._id || r.userId)),
  );
  return toAdd.length ? [...(existing || []), ...toAdd] : existing || [];
};

/**
 * Hook to manage message caching per channel
 * Stores messages and tracks which pages have been loaded
 */
const useMessageCache = () => {
  // Cache: channelId -> { messages: [], loadedPages: Set<number>, oldestPage: number, newestPage: number }
  const cacheRef = useRef(new Map());

  /**
   * Get cached messages for a channel
   */
  const getCachedMessages = useCallback((channelId) => {
    const cached = cacheRef.current.get(channelId);
    return cached?.messages || [];
  }, []);

  /**
   * Get which pages have been loaded for a channel
   */
  const getLoadedPages = useCallback((channelId) => {
    const cached = cacheRef.current.get(channelId);
    return cached?.loadedPages || new Set();
  }, []);

  /**
   * Check if a specific page has been loaded
   */
  const isPageLoaded = useCallback(
    (channelId, page) => {
      const loadedPages = getLoadedPages(channelId);
      return loadedPages.has(page);
    },
    [getLoadedPages],
  );

  /**
   * Add messages to cache (for a specific page)
   * Merges with existing messages, avoiding duplicates
   */
  const addMessages = useCallback((channelId, newMessages, page) => {
    const cached = cacheRef.current.get(channelId) || {
      messages: [],
      loadedPages: new Set(),
      oldestPage: null,
      newestPage: null,
    };

    // Mark page as loaded
    cached.loadedPages.add(page);

    // Merge messages, avoiding duplicates
    const existingIds = new Set(cached.messages.map((m) => m._id || m.tempId));
    const uniqueNewMessages = newMessages.filter(
      (m) => !existingIds.has(m._id || m.tempId),
    );

    // Combine and sort by createdAt (oldest first)
    const allMessages = [...cached.messages, ...uniqueNewMessages].sort(
      (a, b) => {
        const timeA = new Date(a.createdAt || 0).getTime();
        const timeB = new Date(b.createdAt || 0).getTime();
        return timeA - timeB;
      },
    );

    cached.messages = allMessages;

    // Update page tracking
    if (cached.oldestPage === null || page < cached.oldestPage) {
      cached.oldestPage = page;
    }
    if (cached.newestPage === null || page > cached.newestPage) {
      cached.newestPage = page;
    }

    cacheRef.current.set(channelId, cached);
    return allMessages;
  }, []);

  /**
   * Add a single new message (from real-time socket)
   * Adds to the end if newer than existing messages
   */
  const addNewMessage = useCallback((channelId, message) => {
    const cached = cacheRef.current.get(channelId);
    if (!cached) {
      // No cache yet, create it
      cacheRef.current.set(channelId, {
        messages: [message],
        loadedPages: new Set(),
        oldestPage: null,
        newestPage: null,
      });
      return [message];
    }

    // Check if message already exists (by _id or tempId)
    const exists = cached.messages.some((m) => {
      if (message._id && m._id === message._id) return true;
      if (message.tempId && m.tempId === message.tempId) return true;
      return false;
    });
    if (exists) {
      return cached.messages;
    }

    // Add message and sort
    const allMessages = [...cached.messages, message].sort((a, b) => {
      const timeA = new Date(a.createdAt || 0).getTime();
      const timeB = new Date(b.createdAt || 0).getTime();
      return timeA - timeB;
    });

    cached.messages = allMessages;
    cacheRef.current.set(channelId, cached);
    return allMessages;
  }, []);

  /**
   * Update a message (e.g., status change)
   * If updates contains tempId: undefined, it removes the tempId (replacing temp message with real one)
   */
  const updateMessage = useCallback((channelId, messageId, updates) => {
    const cached = cacheRef.current.get(channelId);
    if (!cached) return [];

    cached.messages = cached.messages.map((m) => {
      if (m._id === messageId || m.tempId === messageId) {
        const updated = { ...m, ...updates };

        // ── Status priority: never downgrade ──────────────────────────────
        if (m.status !== undefined && updates.status !== undefined) {
          const existingPri = STATUS_PRIORITY[m.status] ?? -1;
          const incomingPri = STATUS_PRIORITY[updates.status] ?? -1;
          if (incomingPri < existingPri) {
            updated.status = m.status; // keep the higher status
          }
        }

        // ── readBy: always union-merge, never overwrite ───────────────────
        if (updates.readBy !== undefined) {
          updated.readBy = mergeReadByArrays(m.readBy, updates.readBy);
        }

        // Remove tempId if explicitly cleared
        if (updates.tempId === undefined) {
          delete updated.tempId;
        }
        return updated;
      }
      return m;
    });
    cacheRef.current.set(channelId, cached);
    return cached.messages;
  }, []);

  /**
   * Clear cache for a channel
   */
  const clearCache = useCallback((channelId) => {
    cacheRef.current.delete(channelId);
  }, []);

  /**
   * Clear all cache
   */
  const clearAllCache = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  /**
   * Get the oldest loaded page number
   */
  const getOldestPage = useCallback((channelId) => {
    const cached = cacheRef.current.get(channelId);
    return cached?.oldestPage ?? null;
  }, []);

  /**
   * Check if there might be more messages (if we've loaded page 1, there might be more)
   */
  const hasMoreMessages = useCallback((channelId) => {
    const cached = cacheRef.current.get(channelId);
    if (!cached) return true; // Unknown, assume yes
    // If oldestPage is 1, there might be more (page 2, 3, etc.)
    // If oldestPage > 1, we've loaded older pages, so there might be more before that
    return true; // Always assume there might be more unless we explicitly track total count
  }, []);

  return {
    getCachedMessages,
    getLoadedPages,
    isPageLoaded,
    addMessages,
    addNewMessage,
    updateMessage,
    clearCache,
    clearAllCache,
    getOldestPage,
    hasMoreMessages,
  };
};

export { mergeReadByArrays };
export default useMessageCache;
